import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Eye, Trash, PencilSimple, Trophy, Sparkle,
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const C = {
  bg: '#0a0a0a', surface: '#141414',
  border: 'rgba(255,255,255,0.06)', borderHi: 'rgba(255,255,255,0.12)',
  borderLime: 'rgba(200,241,53,0.32)',
  lime: '#c8f135', limeText: '#04050a',
  text: '#f0f0e8', dim: 'rgba(240,240,232,0.65)', faint: 'rgba(240,240,232,0.35)',
};
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const DISP = "'Syne', system-ui, sans-serif";

const Kicker = ({ children, lime }) => (
  <p style={{
    fontFamily: MONO, fontSize: 10, letterSpacing: '0.22em',
    color: lime ? C.lime : C.faint, textTransform: 'uppercase',
    fontWeight: 700, marginBottom: 10,
  }}>{children}</p>
);

/* ── Instant Mode entry form — only 2 inputs by default ── */
const InstantForm = ({ onGenerate, loading }) => {
  const [subject, setSubject] = useState('');
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  const submit = () => {
    if (subject.trim().length < 2) { toast.error('Tell us who this story is about.'); return; }
    if (outcome.trim().length < 4) { toast.error('Add a one-line outcome — the headline win.'); return; }
    onGenerate({ subject_name: subject.trim(), headline_outcome: outcome.trim(), context_notes: notes.trim() });
  };

  return (
    <div data-testid="case-study-instant-form">
      <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 5vw, 52px)' }}>
        <Kicker lime>Case study · 3 credits</Kicker>
        <h1 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(28px, 5vw, 50px)', letterSpacing: '-0.035em', color: C.text, lineHeight: 1.05, marginBottom: 14 }}>
          Tell the story.
        </h1>
        <p style={{ fontFamily: MONO, fontSize: 13.5, color: C.dim, lineHeight: 1.8, maxWidth: 540, margin: '0 auto' }}>
          Two lines is enough. We&apos;ll write a publication-ready case study in your brand voice.
        </p>
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 'clamp(26px, 4vw, 38px)' }}>
        <div style={{ marginBottom: 22 }}>
          <p style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 10, fontWeight: 700 }}>
            Whose story is this?
          </p>
          <input
            type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
            data-testid="case-study-subject-input"
            placeholder="e.g. Meadow Studio, North Coast, a client name…"
            style={{
              width: '100%', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`,
              color: C.text, padding: '14px 16px', borderRadius: 10,
              fontFamily: MONO, fontSize: 14.5, outline: 'none',
            }}
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <p style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 10, fontWeight: 700 }}>
            What was the result?
          </p>
          <input
            type="text" value={outcome} onChange={(e) => setOutcome(e.target.value)}
            data-testid="case-study-outcome-input"
            placeholder='e.g. "Doubled bookings in 60 days" or "Sold out in 4 hours"'
            style={{
              width: '100%', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`,
              color: C.text, padding: '14px 16px', borderRadius: 10,
              fontFamily: MONO, fontSize: 14.5, outline: 'none',
            }}
          />
        </div>

        {!showNotes ? (
          <button onClick={() => setShowNotes(true)} data-testid="case-study-add-context-btn"
            style={{ background: 'transparent', color: C.dim, border: 'none', cursor: 'pointer',
              fontFamily: MONO, fontSize: 11.5, letterSpacing: '0.06em',
              padding: '4px 0', marginBottom: 22, textDecoration: 'underline',
              textUnderlineOffset: 4, textDecorationColor: 'rgba(240,240,232,0.15)',
            }}>
            + Add details (optional)
          </button>
        ) : (
          <div style={{ marginBottom: 22 }}>
            <p style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 10, fontWeight: 700 }}>
              Anything else worth including?
            </p>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
              data-testid="case-study-notes-input"
              placeholder="Quotes, specific numbers, before/after details, customer industry… all optional."
              style={{
                width: '100%', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`,
                color: C.text, padding: '14px 16px', borderRadius: 10,
                fontFamily: MONO, fontSize: 13.5, outline: 'none', resize: 'vertical', lineHeight: 1.6,
              }}
            />
          </div>
        )}

        <button onClick={submit} disabled={loading} data-testid="case-study-generate-btn"
          style={{
            background: C.lime, color: C.limeText, border: 'none',
            cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
            padding: '14px 28px', borderRadius: 10, width: '100%',
            fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
          {loading ? 'Writing…' : 'Write the case study →'}
        </button>
      </div>
    </div>
  );
};

/* ── Generation progress ── */
const STEPS = [
  'Reading the brief',
  'Structuring the narrative',
  'Writing in your voice',
  'Polishing the result',
  'Done',
];

const GenerationProgress = ({ done }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (done) { setTick(STEPS.length); return; }
    const t = setInterval(() => setTick((n) => Math.min(STEPS.length - 1, n + 1)), 2000);
    return () => clearInterval(t);
  }, [done]);
  return (
    <div data-testid="case-study-progress" style={{ maxWidth: 480, margin: '0 auto', textAlign: 'center', padding: '60px 0' }}>
      <Kicker lime>Writing</Kicker>
      <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(26px, 4vw, 38px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 36 }}>
        Building the story…
      </h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
        {STEPS.map((s, i) => {
          const isDone = i < tick;
          const isCurrent = i === tick && !done;
          return (
            <li key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '11px 14px', marginBottom: 6, borderRadius: 8,
              background: isCurrent ? 'rgba(200,241,53,0.05)' : 'transparent',
              opacity: isDone || isCurrent ? 1 : 0.4,
            }}>
              <span style={{
                width: 18, height: 18, borderRadius: '50%',
                background: isDone ? C.lime : 'transparent',
                border: isDone ? 'none' : `1.5px solid ${isCurrent ? C.lime : C.borderHi}`,
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                {isDone && <Check size={11} weight="bold" color={C.limeText} />}
                {isCurrent && <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.lime, animation: 'sclCSPulse 1s ease-in-out infinite' }} />}
              </span>
              <span style={{ fontFamily: MONO, fontSize: 13.5, color: isDone || isCurrent ? C.text : C.faint }}>{s}</span>
            </li>
          );
        })}
      </ul>
      <style>{`@keyframes sclCSPulse { 0%,100% { opacity: 0.4 } 50% { opacity: 1 } }`}</style>
    </div>
  );
};

/* ── Output view ── */
const PostBody = ({ md }) => {
  const blocks = (md || '').split(/\n{2,}/).map((b, i) => {
    const t = b.trim();
    if (t.startsWith('## ')) return { type: 'h2', text: t.replace(/^## /, ''), key: i };
    if (t.startsWith('- ') || t.startsWith('* ')) return { type: 'li', text: t.replace(/^[-*] /, ''), key: i };
    return { type: 'p', text: t, key: i };
  }).filter(b => b.text);
  return (
    <div data-testid="case-study-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {blocks.map((b) => b.type === 'h2' ? (
        <h2 key={b.key} style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(20px, 2.5vw, 25px)',
          letterSpacing: '-0.018em', color: C.text, lineHeight: 1.2, marginTop: 12, marginBottom: -6 }}>{b.text}</h2>
      ) : b.type === 'li' ? (
        <p key={b.key} style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 16.5, lineHeight: 1.78,
          color: 'rgba(240,240,232,0.85)', paddingLeft: 18, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 0, color: C.lime, fontWeight: 800 }}>·</span>
          {b.text}
        </p>
      ) : (
        <p key={b.key} style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 16.5, lineHeight: 1.78,
          color: 'rgba(240,240,232,0.85)' }}>{b.text}</p>
      ))}
    </div>
  );
};

const Output = ({ post, onUpdate, onBack }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/blog-post/${post.id}/publish`);
      onUpdate(data.post);
      toast.success('Published — your case study is live.');
      if (data.public_url) window.open(data.public_url, '_blank', 'noopener');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Publish failed';
      if (typeof detail === 'string' && detail.toLowerCase().includes('public blog is not enabled')) {
        toast.error(detail, { action: { label: 'Open Settings', onClick: () => navigate('/dashboard/settings#public-blog') } });
      } else { toast.error(detail); }
    } finally { setBusy(false); }
  };
  const unpublish = async () => {
    if (!window.confirm('Take this case study offline?')) return;
    setBusy(true);
    try { const { data } = await axios.post(`${API_URL}/api/blog-post/${post.id}/unpublish`); onUpdate(data.post); toast.success('Unpublished'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Unpublish failed'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this case study? This cannot be undone.')) return;
    try { await axios.delete(`${API_URL}/api/blog-post/${post.id}`); toast.success('Deleted'); navigate('/dashboard/assets?tab=blog'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const sections = post.sections || {};
  return (
    <div data-testid="case-study-output">
      <button onClick={onBack} data-testid="case-study-back-btn"
        style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer',
          fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em',
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 22 }}>
        <ArrowLeft size={13} /> Back
      </button>

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999,
        background: 'rgba(200,241,53,0.08)', border: `1px solid ${C.borderLime}`, marginBottom: 18 }}>
        <Trophy size={13} weight="duotone" color={C.lime} />
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.lime, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700 }}>
          {post.headline_metric || 'Outcome'}
        </span>
      </div>

      <h1 data-testid="case-study-title" style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(26px, 4vw, 38px)', letterSpacing: '-0.025em', color: C.text, lineHeight: 1.1, marginBottom: 14 }}>
        {post.title}
      </h1>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 28, paddingBottom: 22, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
          About: <span style={{ color: C.text }}>{post.subject_name || '—'}</span>
        </span>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
          {post.estimated_read_minutes || 5} min read
        </span>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.faint, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
          {(post.seo_checks?.word_count) || 0} words
        </span>
      </div>

      <p style={{ fontFamily: MONO, fontSize: 14, color: C.dim, lineHeight: 1.8, marginBottom: 30 }}>
        {post.meta_description}
      </p>

      <PostBody md={[sections.challenge_md, sections.approach_md, sections.result_md, sections.lessons_md].filter(Boolean).join('\n\n')} />

      {post.quality_warnings && post.quality_warnings.length > 0 && (
        <div style={{ marginTop: 28, padding: '14px 18px', borderRadius: 12, background: 'rgba(252,211,77,0.06)', border: '1px solid rgba(252,211,77,0.28)' }}>
          <p style={{ fontFamily: MONO, fontSize: 10, color: '#fcd34d', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>
            Quality nudge
          </p>
          {post.quality_warnings.map((w, i) => (
            <p key={i} style={{ fontFamily: MONO, fontSize: 12, color: 'rgba(252,211,77,0.85)', lineHeight: 1.6 }}>{w}</p>
          ))}
        </div>
      )}

      <div style={{ position: 'sticky', bottom: 0, marginTop: 36, padding: '16px 0',
        background: 'linear-gradient(180deg, transparent 0%, #0a0a0a 30%)',
        display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ flex: 1 }} />
        {post.status === 'published' ? (
          <>
            <a href={post.public_url} target="_blank" rel="noopener noreferrer" data-testid="case-study-view-live-link"
              style={{ background: 'transparent', color: C.lime, border: `1px solid ${C.borderLime}`,
                padding: '12px 22px', borderRadius: 10, textDecoration: 'none',
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Eye size={13} weight="bold" /> View live →
            </a>
            <button onClick={unpublish} disabled={busy} data-testid="case-study-unpublish-btn"
              style={{ background: 'transparent', color: '#f87171', border: '1px solid rgba(248,113,113,0.32)', cursor: busy ? 'wait' : 'pointer',
                padding: '12px 22px', borderRadius: 10,
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Unpublish</button>
          </>
        ) : (
          <button onClick={publish} disabled={busy} data-testid="case-study-publish-btn"
            style={{ background: C.lime, color: C.limeText, border: 'none', cursor: busy ? 'wait' : 'pointer',
              padding: '12px 22px', borderRadius: 10,
              fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            <Eye size={13} weight="bold" style={{ display: 'inline-block', marginRight: 6, verticalAlign: '-2px' }} />
            {busy ? 'Publishing…' : 'Publish →'}
          </button>
        )}
        <button onClick={remove} data-testid="case-study-delete-btn"
          style={{ background: 'transparent', color: '#f87171', border: '1px solid rgba(248,113,113,0.32)',
            padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
            fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          <Trash size={13} weight="bold" />
        </button>
      </div>
    </div>
  );
};

/* ── Page wrapper ── */
export default function CaseStudyCreatorPage() {
  const [phase, setPhase] = useState('input'); // 'input' | 'generating' | 'output'
  const [post, setPost] = useState(null);
  const [done, setDone] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const postId = searchParams.get('id');
    if (!postId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/blog-post/${postId}`);
        setPost(data.post); setPhase('output');
      } catch { /* fall through */ }
    })();
  }, [searchParams]);

  const generate = async (payload) => {
    setDone(false);
    setPhase('generating');
    try {
      const { data } = await axios.post(`${API_URL}/api/case-study/generate`, payload);
      setPost(data.post);
      setDone(true);
      setTimeout(() => setPhase('output'), 900);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed';
      toast.error(typeof detail === 'string' ? detail : 'Failed');
      setPhase('input');
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="case-study-creator" style={{
        background: C.bg, color: C.text, minHeight: '100vh',
        padding: 'clamp(36px, 5vw, 56px) clamp(20px, 4vw, 40px)',
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          {phase === 'input' && <InstantForm onGenerate={generate} />}
          {phase === 'generating' && <GenerationProgress done={done} />}
          {phase === 'output' && post && <Output post={post} onUpdate={setPost} onBack={() => setPhase('input')} />}
        </div>
      </div>
    </DashboardLayout>
  );
}
